/**
 * Tipos de las pantallas que cierran la categoría **Gestión**:
 * Créditos, Clientes APP y Administración.
 */

/* ------------------------------- Créditos -------------------------------- */

/** Estado de la línea de crédito de un cliente. */
export interface Credito {
  id: number;
  numero_doc: string;
  cliente: string;
  nombre_comercial: string | null;
  producto: string;
  tipo_credito: string | null;
  estado_id: number | null;
  estado_pago: string | null;
  fecha_vencimiento: string | null;
  linea: number;
  cargos: number;
  retencion: number;
  pagos: number;
  disponible: number;
  balance: number;
  vencido: number;
  por_canjear: number;
  vales_sin_liquidar: number;
  facturas: number;
  vencidas: number;
}

/** Contadores del estado de créditos. */
export interface ResumenCreditos {
  total: number;
  filtrados: number;
  linea: number;
  cargos: number;
  pagos: number;
  disponible: number;
  balance: number;
  vencido: number;
  por_canjear: number;
  con_vencido: number;
  con_balance: number;
}

/** Respuesta del listado de créditos. */
export interface CreditosRespuesta {
  creditos: Credito[];
  desde: string;
  hasta: string;
  resumen: ResumenCreditos;
}

/** Factura a crédito del detalle. */
export interface FacturaCredito {
  id: number;
  comprobante: string;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  total: number;
  costo_comprobante: number;
  retencion: number;
  pendiente: number;
  pagado: number;
  saldo: number;
  vencida: boolean;
  vencido: number;
  observaciones: string | null;
  estado_sunat: string | null;
}

/** Pago del detalle de un crédito. */
export interface PagoCredito {
  referencia: string | null;
  monto: number;
  fecha: string | null;
  /** Descripción legible del reparto del pago (la columna es JSON). */
  detalle: string | null;
  estado: string | null;
  metodo: string | null;
  cuenta: string | null;
}

/** Descuento por producto del crédito (`credito.descuento`). */
export interface DescuentoCredito {
  producto_id: number;
  descuento: number;
}

/** Detalle de un crédito, con sus datos editables. */
export interface CreditoDetalleRespuesta {
  credito: Credito & {
    tipo_producto: string | null;
    producto_id: number;
    unidad: string | null;
    precio: number;
    cantidad: number;
    flexibilidad: number;
    /** `credito.total`, el límite de la línea. */
    limite: number;
    aplica_descuento: boolean;
    descuentos: DescuentoCredito[];
    external_code_edsuite: string | null;
    credito_estado_id: number | null;
    credito_estado: string | null;
    credito_estado_color: string | null;
    telefono: string | null;
    direccion: string | null;
    correo: string | null;
    withholding: boolean;
    creado_por: string | null;
    created_at: string | null;
  };
  facturas: FacturaCredito[];
  pagos: PagoCredito[];
  desde: string;
  hasta: string;
}

/** Estados del crédito y productos del catálogo de combustibles. */
export interface CreditosCatalogosRespuesta {
  estados: { id: number; descripcion: string; color: string | null }[];
  productos: { id: number; nombre: string }[];
  tipos: string[];
}

/** Datos editables de un crédito (`/API/credit/update`). */
export interface EdicionCredito {
  total?: number;
  credito_estado_id?: number;
  aplicar_descuento?: 'SI' | 'NO';
  external_code_edsuite?: string;
  descuento?: DescuentoCredito[];
}

/** Filtros del estado de créditos. */
export interface FiltrosCreditos {
  desde?: string;
  hasta?: string;
  search?: string;
  con_vencido?: string;
  con_balance?: string;
}

/* --------------------------- Balance del cliente ---------------------------
 */

/** Filtros del balance (estado de cuenta) de un crédito. */
export interface FiltrosBalance {
  desde?: string;
  hasta?: string;
}

/** Movimiento del estado de cuenta: cargo de una factura o abono de un pago. */
export interface MovimientoBalance {
  tipo: 'cargo' | 'abono';
  fecha: string | null;
  vencimiento: string | null;
  comprobante: string | null;
  informacion: string | null;
  /** Importe total de la factura. */
  total: number;
  retencion: number;
  /** Pendiente de la factura. */
  pagadero: number;
  /** Importe del abono. */
  pago: number;
  /** Saldo acumulado tras el movimiento. */
  saldo: number;
}

/** Balance (estado de cuenta) del cliente de un crédito (`Credito::balance`). */
export interface BalanceCreditoRespuesta {
  credito: {
    id: number;
    numero_doc: string;
    cliente: string;
    nombre_comercial: string | null;
    direccion: string | null;
    telefono: string | null;
    correo: string | null;
    producto: string;
    tipo_credito: string | null;
    linea: number;
    limite: number;
    flexibilidad: number;
    /** El cliente está sujeto a retención. */
    withholding: boolean;
  };
  resumen: {
    saldo_inicial: number;
    cargos: number;
    retencion: number;
    saldo_al_corte: number;
    pagadero: number;
    abonos: number;
    vencido: number;
    facturas: number;
    pagos: number;
  };
  movimientos: MovimientoBalance[];
  desde: string;
  hasta: string;
}

/* ----------------------------- Canje de vales ----------------------------- */

/** Vale de consumo pendiente de liquidar. */
export interface ValeCanje {
  id: number;
  fecha: string | null;
  /** `serie-id`, como el «Numero» del panel anterior. */
  numero: string;
  producto: string;
  producto_id: number;
  cantidad: number;
  precio: number;
  total: number;
  personal: string | null;
  placa: string | null;
  vale: string | null;
}

/** Filtros de los vales por liquidar. */
export interface FiltrosValesCanje {
  desde?: string;
  hasta?: string;
  placa?: string;
}

/** Vales por liquidar de un crédito (`Credito::settlement`). */
export interface ValesCanjeRespuesta {
  credito: {
    id: number;
    numero_doc: string;
    /** `1`/`DNI` sólo admite boletas. */
    tipo_doc: string | null;
    cliente: string;
    nombre_comercial: string | null;
    producto: string;
    tipo_credito: string | null;
    linea: number;
    limite: number;
    flexibilidad: number;
  };
  vales: ValeCanje[];
  totales: {
    vales: number;
    cantidad: number;
    importe: number;
    /** Todo lo que el crédito tiene sin liquidar. */
    por_canjear: number;
    vales_por_canjear: number;
  };
  /** Vales sin producto que el listado del original tampoco muestra. */
  sin_producto: { vales: number; importe: number };
  desde: string;
  hasta: string;
}

/** Tipos de liquidación del panel anterior (`settlement.php`). */
export const TIPOS_LIQUIDACION: readonly { valor: number; etiqueta: string }[] = [
  { valor: 3, etiqueta: 'Factura por Total' },
  { valor: 1, etiqueta: 'Factura por Despacho' },
  { valor: 2, etiqueta: 'Factura por Producto' },
  { valor: 4, etiqueta: 'Factura por Placa' },
  { valor: 5, etiqueta: 'Factura por Transacción (1 factura)' },
  { valor: 6, etiqueta: 'Factura por Producto y Precio (1 factura)' },
] as const;

/** Comprobante que se emitiría en el canje. */
export interface ComprobanteCanje {
  etiqueta: string;
  detalle: string;
  vales: number;
  cantidad: number;
  importe: number;
}

/** Pre-cálculo del canje (`API/credit/settlement`). */
export interface PrecalculoCanjeRespuesta {
  tipo: number;
  tipo_descripcion: string;
  vales: number;
  total_comprobantes: number;
  total_cantidad: number;
  total_importe: number;
  /** Placas de los vales elegidos agrupadas por producto. */
  placas_por_producto: PlacasPorProducto[];
  /** Condición de pago con la que saldría el comprobante. */
  condicion: CondicionCanje;
  comprobantes: ComprobanteCanje[];
}

/** Placas de los vales de un producto (editables antes de facturar). */
export interface PlacasPorProducto {
  producto_id: number;
  producto: string;
  placas: string[];
}

/** Petición del pre-cálculo del canje, con las condiciones de pago. */
export interface PeticionPrecalculoCanje {
  tipo: number;
  vales: number[];
  /** `CREDITO` o `CONTADO`. */
  payment: string;
  /** Plazo en días cuando el pago es a crédito. */
  credit_term?: number;
  /** Fecha de emisión (`AAAA-MM-DD`), limitada a los últimos días de boleta masiva. */
  issue_date: string;
  /** `FACTURA` o `BOLETA`. */
  voucher_type: string;
  voucher_serie?: string;
  /** `caja_chica` o `transferencia` cuando el pago es al contado. */
  contado_destino?: string;
  contado_referencia?: string;
  contado_banco_id?: number;
  contado_referencia_bancaria?: string;
  /** Reemplaza la placa de todos los vales por una (`override_plate`). */
  override_plate?: boolean;
  plate?: string;
  /** Reemplaza el texto de los vales (`override_additional`). */
  override_additional?: boolean;
  additional?: string;
  /** Placas editadas por producto: `{ producto_id: ['AAA111'] }`. */
  edited_plates?: Record<string, string[]>;
}

/** Opciones del alta de una línea de crédito. */
export interface CatalogosCreditoRespuesta {
  tipos_credito: string[];
  productos: { id: number; nombre: string }[];
  detalles_despacho: string[];
  tipos_producto: OpcionCanje[];
  estados: { id: number; descripcion: string; color: string | null }[];
  plazos_vencimiento: number[];
}

/** Petición del alta de una línea de crédito (`Credito::post`). */
export interface NuevaLineaCredito {
  cliente_id: number;
  producto_id: number;
  tipo_credito: string;
  total: number;
  flexibilidad: number;
  aplicar_descuento: string;
  detalle_despacho?: string;
  fecha_vencimiento?: string;
  external_code_edsuite?: string;
  precio?: number;
  cantidad?: number;
  descuento?: string;
}

/** Movimiento de flexibilidad de la línea. */
export interface MovimientoFlexibilidad {
  id: number;
  cantidad: number;
  detalle: string | null;
  estado: number;
  fecha: string | null;
  usuario: string | null;
}

/** Flexibilidad de un crédito. */
export interface FlexibilidadRespuesta {
  flexibilidad: number;
  movimientos: MovimientoFlexibilidad[];
}

/** Petición de la liquidación de vales (emisión de comprobantes). */
export interface PeticionLiquidarCanje extends PeticionPrecalculoCanje {
  /** Tipo de liquidación (1 despacho … 6 producto y precio). */
  tipo: number;
}

/** Comprobante emitido por la liquidación. */
export interface ComprobanteEmitido {
  venta_id: number;
  comprobante_id: number;
  /** `serie-número`, p. ej. `FE05-39`. */
  comprobante: string;
  numero: number;
  total: number;
  retencion: number;
  vales: number;
  lineas: number;
}

/** Resultado de la liquidación de vales. */
export interface LiquidacionRespuesta {
  tipo: number;
  tipo_descripcion: string;
  condicion: CondicionCanje;
  vales: number;
  total_comprobantes: number;
  total_importe: number;
  comprobantes: ComprobanteEmitido[];
  mensaje: string;
}

/** Cuenta bancaria de destino del pago al contado. */
export interface CuentaBancaria {
  id: number;
  descripcion: string | null;
  numero: string | null;
}

/** Opción de un selector del canje. */
export interface OpcionCanje {
  valor: string;
  etiqueta: string;
}

/** Opciones de la liquidación (`Credito::settlement`). */
export interface ParametrosCanjeRespuesta {
  plazos: number[];
  metodos_pago: { valor: string; etiqueta: string; plazo_por_defecto?: number }[];
  destinos: OpcionCanje[];
  cuentas: CuentaBancaria[];
  /** Días hacia atrás con los que se puede emitir una boleta masiva. */
  dias_boleta_masiva: number;
  series_por_defecto: { FACTURA: string | null; BOLETA: string | null };
  /** El selector de IGV (Ley 27037) sólo aplica si la instalación lo activa. */
  ley_27037: boolean;
}

/** Condiciones con las que se emitiría el comprobante del canje. */
export interface CondicionCanje {
  payment: string;
  voucher_type: string;
  voucher_serie: string | null;
  fecha_emision: string;
  fecha_vencimiento: string;
  plazo: number | null;
  destino_pago: string | null;
  referencia: string | null;
  cuenta: CuentaBancaria | null;
  referencia_bancaria: string | null;
  exonerado: string;
}

/* -------------------- Pagos / Liquidación de facturas -------------------- */

/** Filtros del listado de pagos del crédito. */
export interface FiltrosPagos {
  desde?: string;
  hasta?: string;
}

/** Estados con los que el reporte general clasifica cada factura. */
export type EstadoFacturaCredito = 'Pagado' | 'Vencido' | 'Por vencer' | 'Vigente';

/** Filtros del reporte general de pagos (`Credito::report_pagos`). */
export interface FiltrosReportePagos {
  desde?: string;
  hasta?: string;
  /** `todos` o el estado exacto (`Pagado`, `Vencido`, `Por vencer`, `Vigente`). */
  estado?: string;
}

/** Fila del reporte general de pagos: una factura a crédito con su estado. */
export interface FacturaReportePagos {
  id: number;
  venta_id: number;
  comprobante: string;
  cliente: string | null;
  numero_doc: string | null;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  total: number;
  costo_comprobante: number;
  retencion: number;
  pendiente: number;
  pagado: number;
  saldo: number;
  vencida: boolean;
  vencido: number;
  observaciones: string | null;
  estado_sunat: string | null;
  estado: EstadoFacturaCredito;
  dias_vencidos: number;
}

/** Bloque del reporte general de pagos con los subtotales de un cliente. */
export interface GrupoReportePagos {
  cliente: string | null;
  numero_doc: string | null;
  facturado: number;
  retencion: number;
  abonado: number;
  pendiente: number;
  vencido: number;
  facturas: FacturaReportePagos[];
}

/** Respuesta del reporte general de pagos de todos los créditos. */
export interface ReportePagosRespuesta {
  desde: string;
  hasta: string;
  estado: string;
  /** Días con los que se considera «Por vencer» una factura. */
  dias_por_vencer: number;
  resumen: {
    facturas: number;
    clientes: number;
    facturado: number;
    retencion: number;
    abonado: number;
    pendiente: number;
    vencido: number;
  };
  clientes: GrupoReportePagos[];
}

/** Factura a crédito pendiente de pago. */
export interface FacturaPorCobrar {
  id: number;
  venta_id: number;
  comprobante: string;
  fecha_emision: string | null;
  fecha_vencimiento: string | null;
  informacion: string | null;
  total: number;
  retencion: number;
  /** `admin_venta.paidout` acumulado. */
  abonado: number;
  /** `admin_venta.pending_amount`. */
  pendiente: number;
  /** Lo que le falta por pagar. */
  saldo: number;
  dias_vencidos: number;
  vencida: boolean;
  estado: string | null;
}

/** Pago registrado del crédito (`movements_account` con `movement_credit`). */
export interface PagoCredito {
  id: number;
  fecha: string | null;
  monto: number;
  metodo: string | null;
  cuenta: string | null;
  cuenta_numero: string | null;
  referencia: string | null;
  tipo: number;
  estado: string | null;
  registrado_por: string | null;
  /** Comprobante del pago en `uploads/creditos/pagos/` (`movement_file`). */
  archivo: string | null;
  /** Reparto del pago entre las facturas (`movement_detail`). */
  reparto: { factura: string | null; monto: number }[];
}

/** Método de pago del catálogo `abc_metodo_pago`. */
export interface MetodoPago {
  id: number;
  nombre: string;
}

/** Posición del crédito (`Credito_model::get_position`). */
export interface PosicionCredito {
  linea: number;
  consumido: number;
  pagado: number;
  pendiente: number;
  disponible: number;
  por_liquidar: number;
}

/** Pagos / liquidación de facturas de un crédito (`Credito::payments`). */
export interface PagosCreditoRespuesta {
  credito: {
    id: number;
    numero_doc: string;
    cliente: string;
    nombre_comercial: string | null;
    direccion: string | null;
    telefono: string | null;
    correo: string | null;
    producto: string;
    tipo_credito: string | null;
    estado_pago: string | null;
    linea: number;
    limite: number;
    flexibilidad: number;
  };
  posicion: PosicionCredito;
  facturas: FacturaPorCobrar[];
  pagos: PagoCredito[];
  metodos: MetodoPago[];
  cuentas: CuentaBancaria[];
  totales: {
    facturas: number;
    total: number;
    abonado: number;
    /** Lo que falta por pagar. */
    pendiente: number;
    vencido: number;
  };
  desde: string;
  hasta: string;
}

/** Petición para registrar un pago del crédito (`API/Payments::credit`). */
export interface PeticionPagoCredito {
  monto: number;
  metodo_pago_id: number;
  cuenta_id: number;
  referencia?: string;
  fecha_pago: string;
  /** `admin_comprobante.id` de las facturas elegidas (vacío = reparto automático). */
  facturas: number[];
  /**
   * Comprobante del pago en base64 (la API no usa `multipart`): se guarda en
   * `uploads/creditos/pagos/` y su nombre queda en `movement_file`.
   */
  archivo?: { nombre: string; contenido_base64: string };
  /**
   * Clave del envío (idempotencia): si el envío se repite (timeout de red), el API
   * devuelve el pago ya registrado en lugar de aplicarlo dos veces.
   */
  clave_idempotencia?: string;
}

/** Resultado del registro de un pago. */
export interface PagoRegistradoRespuesta {
  pago_id: number;
  monto: number;
  fecha: string;
  reparto: { id: string; amount: number; invoice: string }[];
  /** Nombre del comprobante guardado (`null` si el pago fue sin adjunto). */
  archivo: string | null;
  estado_pago: string;
  mensaje: string;
}

/** Series administrativas disponibles para el canje. */
export interface SeriesCanjeRespuesta {
  tipo: string;
  series: string[];
}

/* ----------------------------- Clientes APP ------------------------------ */

/** Descuento de la APP. */
export interface DescuentoApp {
  id: number;
  customer_id: number;
  cliente: string | null;
  cliente_doc: string | null;
  vehicle_id: number | null;
  placa: string | null;
  vehiculo: string | null;
  fuel_grade_id: number | null;
  combustible: string | null;
  discount_type: string;
  discount_amount: number | null;
  min_consumption: number | null;
  max_consumption: number | null;
  valid_from: string | null;
  valid_until: string | null;
  dias_restantes: number | null;
  vencido: boolean;
  status: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** Vehículo registrado en la APP. */
export interface VehiculoApp {
  id: number;
  customer_id: number;
  cliente: string | null;
  cliente_doc: string | null;
  placa: string;
  tipo: string | null;
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  status: boolean;
  descuentos: number;
  created_at: string | null;
}

/**
 * Ficha del cliente de la APP.
 *
 * El catálogo de descuentos devuelve la cartera local de clientes para poder
 * elegir el dueño del descuento o del vehículo.
 */
export interface ClienteApp {
  id: number;
  nombre: string;
  nombre_comercial: string | null;
  numero_doc: string | null;
}

/** Vehículo de la APP con el detalle de su cliente. */
export interface VehiculoAppDetalle {
  id: number;
  customer_id: number;
  license_plate: string;
  vehicle_type: string | null;
  brand: string | null;
  model: string | null;
  year: number | null;
  status: boolean;
  created_at: string | null;
  descuentos: number;
}

/** Vehículos activos de un cliente de la APP. */
export interface VehiculosClienteRespuesta {
  cliente: ClienteApp;
  vehiculos: VehiculoAppDetalle[];
}

/** Catálogos del alta de descuentos y vehículos de la APP. */
export interface CatalogosClientesAppRespuesta {
  grados: { id: number; nombre: string; producto: string | null; precio: number | null }[];
  clientes: ClienteApp[];
  vehiculos: {
    id: number;
    customer_id: number;
    license_plate: string;
    vehicle_type: string | null;
    brand: string | null;
    model: string | null;
  }[];
  tipos: string[];
}

/** Descuento de la APP tal como se envía al alta o a la edición. */
export interface DatosDescuentoApp {
  customer_id: number;
  vehicle_id?: number | null;
  fuel_grade_id: number;
  discount_type: string;
  discount_amount: number;
  min_consumption?: number | null;
  max_consumption?: number | null;
  valid_from?: string;
  valid_until?: string | null;
}

/** Vehículo de la APP tal como se envía al alta. */
export interface DatosVehiculoApp {
  license_plate: string;
  vehicle_type?: string;
  brand?: string;
  model?: string;
  year?: number | null;
}

/** Respuesta de Clientes APP. */
export interface ClientesAppRespuesta {
  descuentos: DescuentoApp[];
  vehiculos: VehiculoApp[];
  grados: { id: number; nombre: string }[];
  tipos: string[];
  resumen: {
    total: number;
    activos: number;
    vencidos: number;
    por_documento: number;
    por_placa: number;
    por_consumo: number;
    clientes: number;
    vehiculos_con_descuento: number;
    vehiculos: number;
    vehiculos_activos: number;
  };
  generado_at: string;
}

/** Filtros de Clientes APP. */
export interface FiltrosClientesApp {
  search?: string;
  discount_type?: string;
  fuel_grade_id?: number;
  customer_id?: number;
  /** `1` vigentes, `todos` sin filtro. */
  vigentes?: string;
  estado?: string;
}

/** Cliente de la APP (vive en el servicio externo). */
export interface ClienteAppGeneral {
  id: number;
  tipo_doc: string;
  numero_doc: string | null;
  nombre: string | null;
  nombre_comercial: string | null;
  direccion: string | null;
  telefono: string | null;
  correo: string | null;
  estado: boolean;
  withholding: boolean;
  withholding_percentage: number | null;
  /**
   * Acceso al **portal del cliente**. No vive en el servicio de la APP: la API
   * lo cruza desde la base local (`cliente`) por número de documento, así que
   * puede venir vacío en clientes sin acceso.
   */
  portal_activo?: boolean;
  portal_tiene_clave?: boolean;
  portal_fecha_registro?: string | null;
  portal_ultimo_acceso?: string | null;
}

/** Datos del alta o edición de un cliente de la APP. */
export interface DatosClienteApp {
  tipo_doc: string;
  numero_doc: string;
  nombre: string;
  nombre_comercial?: string;
  direccion?: string;
  telefono?: string;
  correo?: string;
  estado?: boolean;
  withholding?: boolean;
  withholding_percentage?: number;
  /** `true` deja al cliente entrar al portal del cliente. */
  portal_activo?: boolean;
  /** Contraseña nueva del portal (mínimo 6). Al editar, vacío = no cambiarla. */
  password?: string;
}

/** Respuesta del listado general de clientes de la APP. */
export interface ClientesAppGeneralesRespuesta {
  clientes: ClienteAppGeneral[];
  total: number;
}

/* ---------------------------- Administración ----------------------------- */

/** Métricas del mapa de calor de surtidores. */
export const METRICAS_CALOR = ['transactions', 'amount', 'volume'] as const;

export type MetricaCalor = (typeof METRICAS_CALOR)[number];

/** Celda (día × hora) del mapa de calor. */
export interface CeldaCalor {
  dia: number;
  hora: number;
  transacciones: number;
  monto: number;
  volumen: number;
  surtidores: number;
}

/** Totales del mes por surtidor. */
export interface SurtidorCalor {
  surtidor: number;
  transacciones: number;
  monto: number;
  volumen: number;
  dias_activos: number;
  primera: string | null;
  ultima: string | null;
}

/** Respuesta del mapa de calor. */
export interface MapaCalorRespuesta {
  periodo: { year: number; month: number; etiqueta: string; desde: string; hasta: string };
  metric: MetricaCalor;
  metricas: MetricaCalor[];
  celdas: CeldaCalor[];
  matriz_surtidores: {
    surtidor: number;
    dia: number;
    hora: number;
    transacciones: number;
    monto: number;
    volumen: number;
    promedio_monto: number | null;
    promedio_volumen: number | null;
  }[];
  surtidores: SurtidorCalor[];
  periodos: { year: number; month: number; etiqueta: string; filas: number }[];
  maximo: number;
  totales: {
    transacciones: number;
    monto: number;
    volumen: number;
    surtidores: number;
    dias: number;
    horas: number;
    pico: { dia: number; hora: number; valor: number } | null;
    dia_top: { dia: number; valor: number } | null;
  };
}
